// PATH: src/pages/admin/tabs/MinistryTab.jsx
// VERSION: 2.0 — Foto con CloudinaryImageField + fixes menores

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Heading,
  Spinner,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiSave,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
} from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';
import CloudinaryImageField from '../../../admin/components/CloudinaryImageField';



export default function MinistryTab() {
  const toast = useToast();

  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const sectionHeadingColor = useColorModeValue('brand.700', 'text.default');
  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  const bw = {
    box: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.200', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    input: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _placeholder: { color: useColorModeValue('brand.400', 'text.muted'), fontWeight: 'semibold' },
      _hover: { borderColor: useColorModeValue('brand.400', 'brand.300') },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: {
        bg: 'accent.600',
        borderColor: 'accent.600',
        transform: 'translateY(-1px)',
        boxShadow: 'md',
      },
      _active: {
        transform: 'translateY(0)',
        boxShadow: 'sm',
      },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: useColorModeValue('white', 'whiteAlpha.100'),
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: {
        bg: useColorModeValue('brand.50', 'whiteAlpha.200'),
        borderColor: useColorModeValue('brand.400', 'brand.300'),
      },
    },
  };

  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({
    id: null,
    name: '',
    position: '',
    ministerio: '',
    photo: '',
    phrase: '',
    sort_order: 0,
  });

  async function load() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/ministry/manage');
      setTitle(data?.title || 'Ministerio');
      setMembers(data?.members || []);
    } catch {
      toast({ status: 'error', title: 'Error cargando ministerio' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function saveTitle() {
    try {
      await axiosInstance.put('/ministry/title', { title });
      toast({ status: 'success', title: 'Título actualizado' });
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo actualizar el título' });
    }
  }

  function editRow(r) {
    setForm({
      id: r.id,
      name: r.name || '',
      position: r.position || '',
      ministerio: r.ministerio || '',
      photo: r.photo || '',
      phrase: r.phrase || '',
      sort_order: r.sort_order ?? 0,
    });
  }

  function resetForm() {
    setForm({
      id: null,
      name: '',
      position: '',
      ministerio: '',
      photo: '',
      phrase: '',
      sort_order: 0,
    });
  }

  async function saveMember() {
    const payload = {
      name: form.name.trim(),
      position: form.position.trim(),
      ministerio: form.ministerio.trim(),
      photo: form.photo || null, // ahora puede ser publicId o URL
      phrase: form.phrase || null,
      sort_order: Number(form.sort_order || 0),
    };
    if (!payload.name || !payload.position || !payload.ministerio) {
      toast({
        status: 'warning',
        title: 'Nombre, posición y ministerio son requeridos',
      });
      return;
    }
    try {
      if (form.id) {
        await axiosInstance.patch(`/ministry/members/${form.id}`, payload);
        toast({ status: 'success', title: 'Miembro actualizado' });
      } else {
        await axiosInstance.post('/ministry/members', payload);
        toast({ status: 'success', title: 'Miembro creado' });
      }
      resetForm();
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo guardar' });
    }
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar miembro?')) return;
    try {
      await axiosInstance.delete(`/ministry/members/${id}`);
      toast({ status: 'success', title: 'Eliminado' });
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar' });
    }
  }

  return (
    <Box {...bw.box} p={4}>
      <Heading fontSize="lg" mb={1} color={headingColor}>
        Ministerio
      </Heading>
      <Box fontSize="sm" mb={4} color={subTextColor}>
        Administra los miembros del ministerio (pastores, líderes, etc.)
      </Box>

      <Button
        {...bw.btnGhost}
        leftIcon={<FiRefreshCw />}
        onClick={load}
        mb={3}
      >
        Refrescar
      </Button>

      <Flex gap={2} align="center" mb={4} wrap="wrap">
        <Input
          {...bw.input}
          placeholder="Título sección"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Button {...bw.btn} leftIcon={<FiSave />} onClick={saveTitle}>
          Guardar título
        </Button>
        <Button {...bw.btnGhost} leftIcon={<FiRefreshCw />} onClick={load}>
          Refrescar
        </Button>
      </Flex>

      <Heading fontSize="md" mb={2} color={sectionHeadingColor}>
        Miembros
      </Heading>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3} mb={3}>
        <Input
          {...bw.input}
          placeholder="Nombre"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        <Input
          {...bw.input}
          placeholder="Posición"
          value={form.position}
          onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
        />
        <Input
          {...bw.input}
          placeholder="Ministerio"
          value={form.ministerio}
          onChange={(e) => setForm((f) => ({ ...f, ministerio: e.target.value }))}
        />
        <Input
          {...bw.input}
          placeholder="Frase (opcional)"
          value={form.phrase}
          onChange={(e) => setForm((f) => ({ ...f, phrase: e.target.value }))}
        />
        <Input
          {...bw.input}
          type="number"
          placeholder="Orden"
          value={form.sort_order}
          onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
        />
      </SimpleGrid>

      <Box mb={3}>
        <CloudinaryImageField
          label="Foto (opcional)"
          value={form.photo}
          onChange={(v) => setForm((f) => ({ ...f, photo: v }))}
          folder="ministry"
          previewW={360}
          previewH={216}
        />
      </Box>

      <Flex gap={2} mb={4} wrap="wrap">
        <Button {...bw.btn} leftIcon={<FiSave />} onClick={saveMember}>
          {form.id ? 'Actualizar' : 'Crear'}
        </Button>
        {form.id && (
          <Button {...bw.btnGhost} onClick={resetForm}>
            Cancelar
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={8}>
          <Spinner thickness="3px" speed="0.65s" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            sx={{ 'th, td, tr, thead, tbody': { borderColor: 'brand.200' } }}
            border="1px solid"
            borderColor="brand.200"
          >
            <Thead bg="brand.100">
              <Tr>
                <Th borderColor="brand.200">ID</Th>
                <Th borderColor="brand.200">Nombre</Th>
                <Th borderColor="brand.200">Posición</Th>
                <Th borderColor="brand.200">Ministerio</Th>
                <Th borderColor="brand.200">Orden</Th>
                <Th borderColor="brand.200">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {members.map((m) => (
                <Tr key={m.id} _hover={{ bg: 'brand.50' }}>
                  <Td borderColor="brand.200">{m.id}</Td>
                  <Td borderColor="brand.200">{m.name}</Td>
                  <Td borderColor="brand.200">{m.position}</Td>
                  <Td borderColor="brand.200">{m.ministerio}</Td>
                  <Td borderColor="brand.200">{m.sort_order}</Td>
                  <Td borderColor="brand.200" whiteSpace="nowrap">
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      mr={2}
                      leftIcon={<FiEdit2 />}
                      onClick={() => editRow(m)}
                    >
                      Editar
                    </Button>
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      leftIcon={<FiTrash2 />}
                      onClick={() => remove(m.id)}
                    >
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))}
              {members.length === 0 && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={4} borderColor="brand.200">
                    No hay miembros registrados.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
